import type { SceneView, ShotView, Style } from "@dramapie/shared";

import { SHOT_STATUS_META, type Tone } from "./status";

/**
 * 故事板的派生数据。
 *
 * 全部由 `StoryboardView` 现算：不引入任何演示数字（原型里的 ¥1.05 / 4 角色都是编的），
 * 缺字段就返回 0 / 空数组，让调用方决定隐藏。
 */

export interface StatusCounts {
  total: number;
  approved: number;
  review: number;
  pending: number;
  error: number;
}

export function countByStatus(shots: readonly ShotView[]): StatusCounts {
  const counts: StatusCounts = { total: shots.length, approved: 0, review: 0, pending: 0, error: 0 };
  for (const shot of shots) {
    counts[SHOT_STATUS_META[shot.status].tone] += 1;
  }
  return counts;
}

export function totalDuration(shots: readonly ShotView[]): number {
  return shots.reduce((sum, shot) => (Number.isFinite(shot.duration) ? sum + shot.duration : sum), 0);
}

export function totalCost(shots: readonly ShotView[]): number {
  return shots.reduce((sum, shot) => (Number.isFinite(shot.costPoints) ? sum + shot.costPoints : sum), 0);
}

export function sceneShots(sceneId: string, shots: readonly ShotView[]): ShotView[] {
  return shots.filter((shot) => shot.sceneId === sceneId);
}

/** 角色只出现在镜头数据里：去重后按首次出现顺序展示。 */
export function distinctChars(shots: readonly ShotView[]): string[] {
  const seen = new Set<string>();
  for (const shot of shots) {
    for (const char of shot.chars) seen.add(char);
  }
  return [...seen];
}

/** 场景封面用该场第一个有签名 URL 的首帧——没有就留空位，不造假图。 */
export function sceneCover(sceneId: string, shots: readonly ShotView[]): ShotView | null {
  const hit = shots.find((shot) => shot.sceneId === sceneId && shot.keyframe?.url);
  return hit ?? null;
}

export function findScene(scenes: readonly SceneView[], sceneId: string): SceneView | undefined {
  return scenes.find((scene) => scene.id === sceneId);
}

export type GateTone = Tone | "idle";

export interface Gate {
  label: string;
  tone: GateTone;
}

/** 顶栏状态胶囊：只从故事板数据推，不额外取数。 */
export function episodeGates(shots: readonly ShotView[]): { visual: Gate; final: Gate } {
  const counts = countByStatus(shots);
  const clips = shots.filter((shot) => shot.clip !== null).length;

  let visual: Gate = { label: "视觉·待生成", tone: "pending" };
  if (counts.total === 0) visual = { label: "视觉·—", tone: "idle" };
  else if (counts.approved === counts.total) visual = { label: "视觉·已确认", tone: "approved" };
  else if (counts.error > 0) visual = { label: "视觉·有失败", tone: "error" };
  else if (counts.review > 0) visual = { label: "视觉·待复核", tone: "review" };

  const final: Gate =
    counts.total > 0 && clips === counts.total
      ? { label: "成片·已确认", tone: "approved" }
      : { label: "成片·待生成", tone: "idle" };

  return { visual, final };
}

/** 场次在故事板里的顺序：按 scene.order；未分场的镜头排在最后。 */
export interface SceneGroup {
  scene: SceneView | null;
  shots: ShotView[];
}

export function groupByScene(
  scenes: readonly SceneView[],
  shots: readonly ShotView[],
): SceneGroup[] {
  const ordered = [...shots].sort((a, b) => a.order - b.order);
  const groups: SceneGroup[] = [];
  for (const scene of [...scenes].sort((a, b) => a.order - b.order)) {
    groups.push({ scene, shots: ordered.filter((shot) => shot.sceneId === scene.id) });
  }
  const known = new Set(scenes.map((scene) => scene.id));
  const orphans = ordered.filter((shot) => !known.has(shot.sceneId));
  if (orphans.length > 0) groups.push({ scene: null, shots: orphans });
  return groups;
}

/** 过滤胶囊：只列出真实存在的四种状态 + 全部。 */
export type ShotFilter = "all" | Tone;

export function matchesFilter(shot: ShotView, filter: ShotFilter): boolean {
  if (filter === "all") return true;
  return SHOT_STATUS_META[shot.status].tone === filter;
}

export const FILTER_LABEL: Record<ShotFilter, string> = {
  all: "全部",
  pending: "待生成",
  review: "待复核",
  approved: "已确认",
  error: "失败",
};

/** 风格枚举 → 中文短标签（eyebrow / 左轨副标题共用）。 */
export const STYLE_LABEL: Record<Style, string> = {
  live_action: "真人实拍",
  anime: "动画",
};

/** 集号：`第01话`（左轨）/ `EP 01`（顶栏）都从 no 来，不补演示数据。 */
export function episodeCode(no: number): string {
  return String(no).padStart(2, "0");
}
